import { motion } from 'framer-motion';
import {
  Share2,
  ImagePlus,
  Send,
  BarChart3,
  Mic2,
  Brain,
  Building2,
  FolderHeart,
  Users,
  Globe,
  Bookmark,
  ImageIcon,
  Layers,
  Calendar,
  Lightbulb,
  Volume2,
} from 'lucide-react';
import { useMobile } from './hooks/useMobile';
import { cn } from '@/lib/utils';

import companyIntelImg from '@/assets/landing/feat_company_intel_real.png';
import multiPlatformImg from '@/assets/landing/feat_multiplatform_real.png';
import imageGenImg from '@/assets/landing/feat_ai_images_real.png';
import carouselImg from '@/assets/landing/feat_carousel_real.png';
import publishImg from '@/assets/landing/feat_direct_publish.png';
import learningImg from '@/assets/landing/feat_dashboard.png';

const features = [
  // Row 1: Two large cards
  {
    title: 'Company Intelligence',
    description:
      'Paste your website URL and Zupa auto-extracts your brand identity, industry, audience, and competitive landscape. Zero manual setup.',
    icon: Globe,
    colSpan: 2,
    color: '#7C3AED',
    hasVisual: true,
    image: companyIntelImg,
  },
  {
    title: 'Multi-Platform Generation',
    description:
      'One input, four platform-perfect outputs. LinkedIn, Instagram, X, and Facebook — each formatted for its unique audience and algorithm.',
    icon: Share2,
    colSpan: 2,
    color: '#60A5FA',
    hasVisual: true,
    image: multiPlatformImg,
  },
  // Row 2: Two large cards
  {
    title: 'AI Image Generation',
    description:
      'Generate stunning AI images for your posts. Get context-aware visuals that match your brand and message perfectly.',
    icon: ImageIcon,
    colSpan: 2,
    color: '#8B5CF6',
    hasVisual: true,
    image: imageGenImg,
  },
  {
    title: 'AI Carousel Builder',
    description:
      'Create multi-slide carousel posts with AI-generated images and text overlays. Plan narrative arcs, then generate slide by slide.',
    icon: Layers,
    colSpan: 2,
    color: '#F59E0B',
    hasVisual: true,
    image: carouselImg,
  },
  // Row 3: Four standard cards
  {
    title: 'AI-Driven Selection',
    description:
      'Every variation gets a psychology-backed quality score with transparent reasoning so you know exactly why it works.',
    icon: BarChart3,
    colSpan: 1,
    color: '#F59E0B',
  },
  {
    title: '15+ Psychology Principles',
    description:
      'Scarcity, social proof, anchoring, loss aversion — applied automatically to every post for maximum engagement.',
    icon: Brain,
    colSpan: 1,
    color: '#7C3AED',
  },
  {
    title: 'Transparent Explanations',
    description:
      'See exactly which psychological principles were applied to each post and why they work for your audience.',
    icon: Mic2,
    colSpan: 1,
    color: '#60A5FA',
  },
  {
    title: 'Post Variations',
    description:
      'Multiple variations per platform, each scored and explained. Pick the one that fits your voice.',
    icon: ImagePlus,
    colSpan: 1,
    color: '#34D399',
  },
  // Row 4: Four standard cards
  {
    title: 'Analytics Dashboard',
    description:
      'Track your generation history, favorite posts, and content performance across platforms.',
    icon: BarChart3,
    colSpan: 1,
    color: '#60A5FA',
  },
  {
    title: 'Content Calendar',
    description:
      'Plan and schedule your content with a visual calendar. Add notes and organize your posting schedule.',
    icon: Calendar,
    colSpan: 1,
    color: '#34D399',
  },
  {
    title: 'Content Ideas',
    description:
      'Never run out of ideas. AI-suggested content topics based on your brand and trending conversations.',
    icon: Lightbulb,
    colSpan: 1,
    color: '#F59E0B',
  },
  {
    title: '10 Tone Presets',
    description:
      'Professional, conversational, enthusiastic, educational, inspirational, humorous, authoritative, storytelling, casual, urgent.',
    icon: Volume2,
    colSpan: 1,
    color: '#EC4899',
  },
  // Row 5: Four standard cards
  {
    title: 'Saved Prompts',
    description:
      'Save and reuse your best-performing prompt templates across workspaces. Build your content playbook.',
    icon: Bookmark,
    colSpan: 1,
    color: '#F59E0B',
  },
  {
    title: 'Single Dashboard',
    description:
      'Manage multiple brands from one account. Each workspace has its own brand profile, tone, and audience.',
    icon: Building2,
    colSpan: 1,
    color: '#34D399',
  },
  {
    title: 'Collections',
    description:
      'Organize generated posts into collections. Reuse and repurpose your best content.',
    icon: FolderHeart,
    colSpan: 1,
    color: '#60A5FA',
  },
  {
    title: 'Audience Personas',
    description:
      'AI-generated audience profiles tailored to your brand. Target different segments with precision.',
    icon: Users,
    colSpan: 1,
    color: '#06B6D4',
  },
  // Row 6: Two large cards
  {
    title: 'Direct Publishing',
    description:
      'Publish to LinkedIn, Instagram, Facebook, and X directly from Zupa. No copy-paste.',
    icon: Send,
    colSpan: 2,
    color: '#7C3AED',
    hasVisual: true,
    image: publishImg,
  },
  {
    title: 'Learning System',
    description:
      'Zupa learns from your edits and preferences. Each post gets smarter than the last.',
    icon: Users,
    colSpan: 2,
    color: '#34D399',
    hasVisual: true,
    image: learningImg,
  },
];

export function FeaturesSection() {
  const isMobile = useMobile();

  return (
    <section
      id="features"
      className="relative overflow-hidden bg-landing-bg-alt"
      style={{
        paddingTop: 'clamp(80px, 10vh, 160px)',
        paddingBottom: 'clamp(80px, 10vh, 160px)',
      }}
    >
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16 text-center">
          <p
            className="mb-4 text-zupa-purple-light"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 'clamp(0.9rem, 1vw, 1.1rem)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.2em',
            }}
          >
            Everything You Need
          </p>
          <h2
            className="text-[#E8E6F0]"
            style={{
              fontFamily: "'Space Grotesk', system-ui, sans-serif",
              fontSize: 'clamp(2rem, 4vw + 0.5rem, 3.75rem)',
              fontWeight: 600,
              lineHeight: 1.1,
            }}
          >
            Dominate Every Platform
          </h2>
        </div>

        {/* Bento grid */}
        <div
          className={cn(
            'grid gap-4',
            isMobile ? 'grid-cols-1' : 'grid-cols-4'
          )}
        >
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{
                  duration: 0.6,
                  delay: i * 0.08,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={
                  !isMobile
                    ? {
                        y: -4,
                        transition: { duration: 0.25 },
                      }
                    : undefined
                }
                className={cn(
                  'group relative overflow-hidden transition-colors duration-300',
                  'border border-landing-border hover:border-[rgba(124,58,237,0.3)]',
                  !isMobile && feature.colSpan === 2 && 'col-span-2',
                  !isMobile && feature.colSpan === 3 && 'col-span-3',
                  !isMobile && feature.colSpan === 1 && 'col-span-1'
                )}
                style={{
                  padding: 'clamp(1.5rem, 3vw, 2.5rem)',
                  background:
                    'linear-gradient(135deg, rgba(124,58,237,0.03), rgba(96,165,250,0.02))',
                  perspective: '800px',
                }}
              >
                {/* Hover glow */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    boxShadow: `inset 0 0 80px ${feature.color}06, 0 0 40px ${feature.color}04`,
                  }}
                />

                {/* Visual area for large cards */}
                {feature.hasVisual && !isMobile && (
                  <div
                    className="relative mb-6 h-[200px] overflow-hidden rounded-lg"
                    style={{ border: '1px solid rgba(232,230,240,0.06)' }}
                  >
                    {feature.image ? (
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="h-full w-full object-cover object-top opacity-85 transition-opacity duration-300 group-hover:opacity-100"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center bg-landing-surface">
                        <Icon
                          size={48}
                          className="opacity-10"
                          style={{ color: feature.color }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Icon */}
                <div
                  className="mb-4 flex h-12 w-12 items-center justify-center"
                  style={{
                    background: `${feature.color}15`,
                    border: `1px solid ${feature.color}25`,
                  }}
                >
                  <Icon size={22} style={{ color: feature.color }} />
                </div>

                {/* Title */}
                <h3
                  className="mb-2 text-[#E8E6F0] font-semibold"
                  style={{
                    fontFamily: "'Space Grotesk', system-ui, sans-serif",
                    fontSize: feature.colSpan >= 2 && !isMobile ? '1.625rem' : '1.35rem',
                  }}
                >
                  {feature.title}
                </h3>

                {/* Description */}
                <p
                  className="text-[#C8C6D2] text-lg leading-relaxed"
                  style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
                >
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
